import { decodeToken, getToken } from "@/src/services/api/auth.api";
import { GraphQLClient } from "graphql-request";
import { Platform } from "react-native";
import { isGraphQLAuthError, isRefreshTokenExpired } from "./auth-error";
import {
  coordinatedRefresh,
  handleAuthFailure,
  MAX_REFRESH_ATTEMPTS,
} from "./token-refresh";

const isWeb = Platform.OS === "web";

const scopeVariablesToOrg = (variables: unknown, orgId: string): unknown => {
  if (!variables || typeof variables !== "object") return variables;
  const vars = variables as Record<string, unknown>;

  // Only JSON-object filters can be scoped. A non-string filter, or a raw id
  // string (e.g. leaderboard-by-user), is left untouched.
  if (typeof vars.filter !== "string") return variables;

  let parsed: unknown;
  try {
    parsed = JSON.parse(vars.filter);
  } catch {
    return variables;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return variables;
  }

  const filterObj = parsed as Record<string, unknown>;
  if ("OrganizationIds" in filterObj) return variables; // already scoped

  return {
    ...vars,
    filter: JSON.stringify({ ...filterObj, OrganizationIds: orgId }),
  };
};

// Matches an insert mutation that takes the whole record as an `input` variable,
// e.g. `insertnews(input: $input)` (news, wiki). Update mutations don't match.
const INSERT_WITH_INPUT = /\binsert\w*\s*\(\s*input:\s*\$input\b/;

const injectOrgIntoMutation = (
  document: unknown,
  variables: unknown,
  orgId: string,
): unknown => {
  if (typeof document !== "string") return variables;

  const vars =
    variables && typeof variables === "object"
      ? { ...(variables as Record<string, unknown>) }
      : ({} as Record<string, unknown>);
  let changed = false;

  if (document.includes("$organizationIds") && !vars.organizationIds) {
    vars.organizationIds = [orgId];
    changed = true;
  }

  if (
    INSERT_WITH_INPUT.test(document) &&
    vars.input &&
    typeof vars.input === "object" &&
    !Array.isArray(vars.input) &&
    !("OrganizationIds" in (vars.input as Record<string, unknown>))
  ) {
    vars.input = {
      ...(vars.input as Record<string, unknown>),
      OrganizationIds: [orgId],
    };
    changed = true;
  }

  return changed ? vars : variables;
};

const gqlClient = async () => {
  const token = await getToken();
  const headers: Record<string, string> = {
    "x-blocks-key": `${process.env.EXPO_PUBLIC_X_BLOCKS_KEY}`,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else if (!isWeb) {
    console.warn("No auth token found");
  }

  const client = new GraphQLClient(
    `${process.env.EXPO_PUBLIC_DATA_GATEWAY_URL}`,
    { headers, credentials: "include" },
  );

  const orgId = (await decodeToken())?.org_id as string | undefined;
  const scoped = !!orgId && orgId !== "default";
  const originalRequest = client.request.bind(client);

  const runWithAuthRetry = async (args: any[]): Promise<unknown> => {
    let attempts = 0;
    for (;;) {
      try {
        return await (originalRequest as (...a: any[]) => unknown)(...args);
      } catch (err: any) {
        if (!isGraphQLAuthError(err)) throw err;

        if (attempts >= MAX_REFRESH_ATTEMPTS) {
          throw err;
        }
        attempts += 1;

        try {
          const data = await coordinatedRefresh();
          if (!isWeb) {
            client.setHeader("Authorization", `Bearer ${data.access_token}`);
          }
        } catch (refreshErr) {
          if (isRefreshTokenExpired(refreshErr)) {
            await handleAuthFailure();
          }
          throw err;
        }
      }
    }
  };

  (client as { request: (...args: any[]) => unknown }).request = (
    ...args: any[]
  ) => {
    if (scoped) {
      const document = args[0];
      let variables = args.length >= 2 ? args[1] : undefined;
      variables = scopeVariablesToOrg(variables, orgId as string);
      variables = injectOrgIntoMutation(document, variables, orgId as string);
      args[1] = variables;
    }
    return runWithAuthRetry(args);
  };

  return client;
};

export default gqlClient;
